import { Role } from 'features/auth/types';
import { PatientRoutes, routes } from 'routes';

import { QuestionnaireCompletionPromis } from 'features/patient/assessment/assessment-controller';

// import { PatientSsoInfo } from 'features/patient/sso-info';
// import { SymptomSso } from 'features/symptoms';
import { AssessmentComplete } from 'features/patient/assessment/assessment-complete';
import { QuestionnaireControlFlow } from 'features/patient/assessment/assessment-controller/questionnaire-control-flow';
import { AssessmentIntro } from 'features/patient/assessment/assessment-intro';
import { PatientOutcomeProgress } from 'features/patient/assessment/assessment-progress';
import { QuestionnaireCompletionEnd } from 'features/patient/assessment/questionnaire-completion/questionnaire-completion-end';
import { PatientCgs } from 'features/patient/assessment/questionnaires/cgs';
import { QuestionnaireDemographic } from 'features/patient/assessment/questionnaires/demographic/questionnaire-demographic';
import { PatientDhi } from 'features/patient/assessment/questionnaires/dhi';
import { QuestionnaireInjury } from 'features/patient/assessment/questionnaires/injury/questionnaire-injury';
import { PatientPcs } from 'features/patient/assessment/questionnaires/pcs';
import { PatientPromis } from 'features/patient/assessment/questionnaires/promis';
import { PatientOnboardingProfile } from 'features/patient/onboarding/page-patientOnboarding';
import { PatientHomeWrapper } from 'features/patient/patient-home/patient-home-v2';
import { PatientSubtypeRange } from 'features/patient/patient-subtype-range/patient-subtype-range';
import { PatientSummary } from 'features/patient/patient-summary';
import { PageScheduleTest } from 'features/clinician/patient/schedule-test/page-schedule-test';
import { PatientTreatmentRecommendations } from 'features/patient/treatment-recommendations';
import { PrescriptionPadExportContainer } from 'features/patient/treatment-recommendations/components/prescription-pad/prescription-pad';
import { TreatmentRecommendationsV2 } from 'features/patient/treatment-recommendations/treatment-recommendations-v2';
import { cloneDeep } from 'lodash';
import { PrivateRouteProps } from 'navigation/router/components/routes/private-route';
import { PatientRouteTypeDef } from './types';
import { InventoryAnalysisPage } from 'features/clinician/patient/inventory-analysis/page-inventory-analysis';

/**
 * Routes that are available during onboarding
 */
export const PatientOnboardingRoutes: PrivateRouteProps[] = assignRoles(
  [
    {
      /**
       * WARN: This is not a test route
       */
      path: PatientRoutes.PatientOnboardingProfile,
      component: PatientOnboardingProfile,
      exact: true
    }
  ],
  [Role.Patient]
);

export const PATIENT_NEEDS_ASSESSMENT_ROUTES: PrivateRouteProps[] = assignRoles(
  [
    {
      /**
       * Assessment introduction page
       * Disabled unless the user has 0 questionnaires completed
       * Otherwise this should be disabled
       */
      path: PatientRoutes.AssessmentStart,
      component: AssessmentIntro
    },
    {
      path: PatientRoutes.Home,
      component: PatientHomeWrapper
    }
  ],
  [Role.Patient]
);

export const PATIENT_IN_ASSESSMENT_ROUTES: PrivateRouteProps[] = assignRoles(
  [
    {
      path: PatientRoutes.PatientCgs,
      component: PatientCgs
    },
    {
      path: PatientRoutes.PatientPromis,
      component: PatientPromis
    },
    {
      path: PatientRoutes.PatientDhi,
      component: PatientDhi
    },
    {
      path: PatientRoutes.PatientPcs,
      component: PatientPcs
    },
    {
      path: PatientRoutes.QuestionnaireInjury,
      component: QuestionnaireInjury
    },
    {
      path: PatientRoutes.QuestionnaireDemographics,
      component: QuestionnaireDemographic
    },
    {
      /**
       * Assessment resume page.
       * Disabled unless the user has at least one questionnare completed
       */
      path: PatientRoutes.AssessmentResume,
      component: PatientOutcomeProgress
      // noTestDataNeeded: true
    },
    {
      /**
       * Assessment resume page.
       * Disabled unless the user has at least one questionnare completed
       */
      path: PatientRoutes.AssessmentControlFlow,
      component: QuestionnaireControlFlow
      // noTestDataNeeded: true
    },
    {
      path: PatientRoutes.Completion,
      component: QuestionnaireCompletionEnd
    },
    {
      path: PatientRoutes.CompletionPromis,
      component: QuestionnaireCompletionPromis
    }
  ],
  [Role.Patient]
);

/**
 * Helper method to assign roles to a group of routes
 * @param routes
 * @param roles
 * @returns
 */
function assignRoles(
  routes: Omit<PrivateRouteProps, 'roles'>[],
  roles: PrivateRouteProps['roles']
) {
  return routes.map(route => ({ ...route, roles }));
}

/**
 * All patient routes that are not test routes
 */
export const PatientOnlyRoutes_DOES_NOT_NEED_ASSESSMENT: PrivateRouteProps[] = [
  {
    path: PatientRoutes.AssessmentComplete,
    component: AssessmentComplete
  },
  {
    path: PatientRoutes.Home,
    component: PatientHomeWrapper
  }
].map((route: Omit<PatientRouteTypeDef, 'roles'>) => ({
  ...route,
  roles: [Role.Patient]
}));

export const PatientAndClinicianRoutes: PrivateRouteProps[] = [
  {
    path: PatientRoutes.Home,
    component: PatientHomeWrapper
  }

  // {
  //   exact: true,
  //   path: PatientRoutes.PatientDemographics,
  //   component: PatientDemographics
  // },
  // {
  //   exact: true,
  //   path: PatientRoutes.PatientSymptom,
  //   component: SymptomSso
  // },
  // {
  //   path: PatientRoutes.PatientDemographicsProfile,
  //   component: PatientDemographicsProfile
  // },

  // {
  //   path: PatientRoutes.PatientSsoInfo,
  //   component: PatientSsoInfo
  // },
  // {
  //   path: PatientRoutes.PatientHowAreYouTracker,
  //   component: PatientHowAreYouTracker
  // },
  // {
  //   path: PatientRoutes.PatientDemographicsInjury,
  //   component: PatientDemographicsInjury
  // },
  // {
  //   path: PatientRoutes.PatientDemographicsPersonalHistory,
  //   component: PatientDemographicsPersonalHistory
  // },
].map((route: Omit<PrivateRouteProps, 'roles'>) => ({
  ...route,
  roles: [Role.ClinicAdmin, Role.Clinician, Role.Patient]
}));

export const PatientClinicianOnlyRoutes: PrivateRouteProps[] = [
  {
    path: PatientRoutes.PatientTreatmentRecommendationsExportPDF,
    component: PrescriptionPadExportContainer
  },
  {
    path: PatientRoutes.PatientTreatmentRecommendations,
    exact: true,
    component: PatientTreatmentRecommendations
  },
  {
    path: PatientRoutes.PatientTreatmentRecommendationsV2,
    exact: true,
    component: TreatmentRecommendationsV2
  },
  {
    exact: true,
    path: PatientRoutes.PatientDashboard,
    component: PatientSummary
  },
  {
    exact: true,
    path: PatientRoutes.PatientSubtypeRange,
    component: PatientSubtypeRange
  },
  {
    exact: true,
    path: PatientRoutes.PatientInventoryItemAnalysis,
    component: InventoryAnalysisPage
  },
  {
    // exact: true,
    path: routes.clinician.patient.assessments,
    component: PageScheduleTest
  },
  ...cloneDeep(PatientAndClinicianRoutes)
].map((route: Omit<PrivateRouteProps, 'roles'>) => ({
  ...route,
  roles: [Role.ClinicAdmin, Role.Clinician]
}));

export const PatientRouteDefinitions: PrivateRouteProps[] = [
  ...PATIENT_IN_ASSESSMENT_ROUTES,
  ...PatientOnlyRoutes_DOES_NOT_NEED_ASSESSMENT,
  ...PatientAndClinicianRoutes
];
